import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';

import { Plus, Edit, Trash2, Search, CheckCircle, Clock, AlertCircle } from 'lucide-react'; // Icons for add/edit/delete

// Import the new modal components
import PhaseModal from '../../components/Projects/Modals/PhaseModal';
import TaskModal from '../../components/Projects/Modals/TaskModal';
import FindContractorsModal from '../../components/Projects/Modals/FindContractorsModal';
import BrowseProsModal from '../../components/Projects/Modals/BrowseProsModalUpdated';

const ItemTypes = {
    TASK: 'task',
    PHASE: 'phase',
};

const Task = ({ task, index, phaseId, moveTask, onEdit, onDelete, onFindContractors }) => {
    const [, ref] = useDrag({
        type: ItemTypes.TASK,
        item: { id: task.id, index, phaseId },
    });

    const [, drop] = useDrop({
        accept: ItemTypes.TASK,
        hover: (draggedItem) => {
            if (draggedItem.id !== task.id) {
                moveTask(draggedItem, { id: task.id, index, phaseId });
                draggedItem.index = index;
                draggedItem.phaseId = phaseId;
            }
        },
    });

    // Determine Task Status Visuals
    let statusIcon = <AlertCircle size={16} color="#ffc107" />;
    let statusText = "Pending";
    let statusClass = "status-pending";

    if (task.status === 'Active') {
        statusIcon = <Clock size={16} color="#007bff" />;
        statusText = "Active";
        statusClass = "status-active";
    } else if (task.status === 'Completed') {
        statusIcon = <CheckCircle size={16} color="#28a745" />;
        statusText = "Completed";
        statusClass = "status-completed";
    }

    return (
        <div ref={(node) => ref(drop(node))} className="task-card">
            <div className="task-header">
                <span className="task-name">{task.name}</span>
                <div className="task-actions-top">
                    <button onClick={() => onEdit(task)} className="icon-button"><Edit size={14} /></button>
                    <button onClick={() => onDelete(task.id, phaseId)} className="icon-button delete-button"><Trash2 size={14} /></button>
                </div>
            </div>
            
            <p className="task-description">{task.description}</p>
            <div className="task-meta">
                <span className="trade-tag">{task.suggestedTrade || "General"}</span>
                <span className={`status-badge ${statusClass}`}>
                    {statusIcon} {statusText}
                </span>
            </div>

            <div className="task-footer">
                {task.status === 'Pending' ? (
                    <button className="find-contractors-btn" onClick={() => onFindContractors(task)}>
                        Find Contractors
                    </button>
                ) : (
                    <div className="active-job-info">
                        <span className="live-job-label">Live Job Active</span>
                        <button className="manage-job-btn" onClick={() => window.open('/jobs-dashboard', '_blank')}>Manage</button>
                    </div>
                )}
            </div>
        </div>
    );
};

const Phase = ({ phase, index, moveTask, movePhase, onAddTask, onEditPhase, onDeletePhase, onEditTask, onDeleteTask, onFindContractors }) => {
    const [, ref] = useDrag({
        type: ItemTypes.PHASE,
        item: { id: phase.id, index },
    });

    const [, drop] = useDrop({
        accept: ItemTypes.PHASE,
        hover: (draggedItem) => {
            if (draggedItem.id !== phase.id) {
                movePhase(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    return (
        <div ref={(node) => ref(drop(node))} className="phase">
            <div className="phase-header">
                <h3>{phase.name}</h3>
                <div className="phase-actions">
                    <button onClick={() => onEditPhase(phase)} className="icon-button"><Edit size={16} /></button>
                    <button onClick={() => onDeletePhase(phase.id)} className="icon-button delete-button"><Trash2 size={16} /></button>
                    <button onClick={() => onAddTask(phase.id)} className="icon-button"><Plus size={16} /></button>
                </div>
            </div>
            <div className="phase-tasks">
                {phase.tasks.map((task, i) => (
                    <Task
                        key={task.id}
                        task={task}
                        index={i}
                        phaseId={phase.id}
                        moveTask={moveTask}
                        onEdit={onEditTask}
                        onDelete={onDeleteTask}
                        onFindContractors={onFindContractors}
                    />
                ))}
            </div>
        </div>
    );
};

const AIProjectManager = () => {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for modals
    const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(null);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [currentPhaseIdForTask, setCurrentPhaseIdForTask] = useState(null);
    
    // New Modals State
    const [isFindContractorsModalOpen, setIsFindContractorsModalOpen] = useState(false);
    const [isBrowseProsModalOpen, setIsBrowseProsModalOpen] = useState(false);
    const [activeTaskForContractors, setActiveTaskForContractors] = useState(null);

    const fetchProject = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/UserProjects/${projectId}`);
            setProject(response.data);
        } catch (err) {
            setError('Failed to fetch project data.');
            console.error('Error fetching project data:', err);
            showToast('error', 'Failed to load project data.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {
            fetchProject();
        }
    }, [projectId]);

    const handleMoveTask = async (from, to) => {
        const newPhases = [...project.phases];
        const fromPhase = newPhases.find(p => p.id === from.phaseId);
        const toPhase = newPhases.find(p => p.id === to.phaseId);

        if (!fromPhase || !toPhase) return;

        const [task] = fromPhase.tasks.splice(from.index, 1);
        toPhase.tasks.splice(to.index, 0, task);

        setProject(prev => ({ ...prev, phases: newPhases }));

        // Persist task reorder/move to backend
        try {
            await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${from.phaseId}/tasks/reorder`, fromPhase.tasks.map(t => t.id));
            if (from.phaseId !== to.phaseId) {
                await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${to.phaseId}/tasks/reorder`, toPhase.tasks.map(t => t.id));
            }
            showToast('success', 'Task reordered successfully!');
        } catch (err) {
            showToast('error', 'Failed to reorder task.');
            console.error('Error reordering task:', err);
            // Revert UI if API fails
            fetchProject();
        }
    };

    const handleMovePhase = async (fromIndex, toIndex) => {
        const newPhases = [...project.phases];
        const [phase] = newPhases.splice(fromIndex, 1);
        newPhases.splice(toIndex, 0, phase);

        setProject(prev => ({ ...prev, phases: newPhases }));

        // Persist phase reorder to backend
        try {
            await axios.post(`${API_BASE_URL}/api/UserProjects/${projectId}/phases/reorder`, newPhases.map(p => p.id));
            showToast('success', 'Phase reordered successfully!');
        } catch (err) {
            showToast('error', 'Failed to reorder phase.');
            console.error('Error reordering phase:', err);
            // Revert UI if API fails
            fetchProject();
        }
    };

    // Phase CRUD operations
    const openAddPhaseModal = () => {
        setCurrentPhase(null); // Clear for new phase
        setIsPhaseModalOpen(true);
    };

    const openEditPhaseModal = (phase) => {
        setCurrentPhase(phase);
        setIsPhaseModalOpen(true);
    };

    const closePhaseModal = () => {
        setIsPhaseModalOpen(false);
        setCurrentPhase(null);
    };

    const handleSavePhase = async (phaseData) => {
        setIsLoading(true);
        try {
            if (phaseData.id) { // Update existing phase
                await axios.put(`${API_BASE_URL}/api/UserProjects/phases/${phaseData.id}`, phaseData);
                showToast('success', 'Phase updated successfully!');
            } else { // Add new phase
                await axios.post(`${API_BASE_URL}/api/UserProjects/${projectId}/phases`, { ...phaseData, projectId: projectId });
                showToast('success', 'Phase added successfully!');
            }
            closePhaseModal();
            fetchProject();
        } catch (err) {
            showToast('error', `Failed to ${phaseData.id ? 'update' : 'add'} phase.`);
            console.error(`Error ${phaseData.id ? 'updating' : 'adding'} phase:`, err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeletePhase = async (phaseId) => {
        if (!window.confirm('Are you sure you want to delete this phase and all its tasks?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/phases/${phaseId}`);
            showToast('success', 'Phase deleted successfully!');
            fetchProject();
        } catch (err) {
            showToast('error', 'Failed to delete phase.');
            console.error('Error deleting phase:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Task CRUD operations
    const openAddTaskModal = (phaseId) => {
        setCurrentTask(null); // Clear for new task
        setCurrentPhaseIdForTask(phaseId);
        setIsTaskModalOpen(true);
    };

    const openEditTaskModal = (task) => {
        setCurrentTask(task);
        setCurrentPhaseIdForTask(task.phaseId); // Ensure phaseId is set for context
        setIsTaskModalOpen(true);
    };

    const closeTaskModal = () => {
        setIsTaskModalOpen(false);
        setCurrentTask(null);
        setCurrentPhaseIdForTask(null);
    };

    const handleSaveTask = async (taskData) => {
        setIsLoading(true);
        try {
            if (taskData.id) { // Update existing task
                await axios.put(`${API_BASE_URL}/api/UserProjects/tasks/${taskData.id}`, taskData);
                showToast('success', 'Task updated successfully!');
            } else { // Add new task
                await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${currentPhaseIdForTask}/tasks`, { ...taskData, phaseId: currentPhaseIdForTask });
                showToast('success', 'Task added successfully!');
            }
            closeTaskModal();
            fetchProject();
        } catch (err) {
            showToast('error', `Failed to ${taskData.id ? 'update' : 'add'} task.`);
            console.error(`Error ${taskData.id ? 'updating' : 'adding'} task:`, err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTask = async (taskId, phaseId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/tasks/${taskId}`);
            showToast('success', 'Task deleted successfully!');
            fetchProject();
        } catch (err) {
            showToast('error', 'Failed to delete task.');
            console.error('Error deleting task:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Contractor Finding Logic
    const openFindContractorsModal = (task) => {
        setActiveTaskForContractors(task);
        setIsFindContractorsModalOpen(true);
    };

    const handlePostJob = () => {
        // Navigate to Job Wizard, pre-filling data
        // We'd pass task info via state or query params
        // For now, let's just close the modal and show a toast (or redirect if wizard supports it)
        setIsFindContractorsModalOpen(false);
        // window.location.href = `/job-creation-wizard?taskId=${activeTaskForContractors.id}`;
        showToast('info', 'Redirecting to Job Wizard... (Not fully implemented in this demo)');
    };

    const handleBrowsePros = () => {
        setIsFindContractorsModalOpen(false);
        setIsBrowseProsModalOpen(true);
    };

    const handleInviteSent = () => {
        // Refresh project to update task status if needed (backend sets task status to Active on invite)
        fetchProject();
    };


    if (isLoading) {
        return <Loader2 />;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    if (!project) {
        return <div className="error-message">Project not found or loaded.</div>;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="project-manager">
                <div className="project-header">
                    <h2>{project.name}</h2>
                    <p>{project.description}</p>
                    <button onClick={openAddPhaseModal} className="add-phase-button">
                        <Plus size={20} /> Add New Phase
                    </button>
                </div>

                <div className="phases-container">
                    {project.phases.map((phase, i) => (
                        <Phase
                            key={phase.id}
                            phase={phase}
                            index={i}
                            moveTask={handleMoveTask}
                            movePhase={handleMovePhase}
                            onAddTask={openAddTaskModal}
                            onEditPhase={openEditPhaseModal}
                            onDeletePhase={handleDeletePhase}
                            onEditTask={openEditTaskModal} // Pass to Phase for Task component
                            onDeleteTask={handleDeleteTask} // Pass to Phase for Task component
                            onFindContractors={openFindContractorsModal}
                        />
                    ))}
                </div>
            </div>

            {/* Phase Modal */}
            {isPhaseModalOpen && (
                <PhaseModal
                    isOpen={isPhaseModalOpen}
                    onClose={closePhaseModal}
                    onSave={handleSavePhase}
                    phase={currentPhase}
                />
            )}

            {/* Task Modal */}
            {isTaskModalOpen && (
                <TaskModal
                    isOpen={isTaskModalOpen}
                    onClose={closeTaskModal}
                    onSave={handleSaveTask}
                    task={currentTask}
                    phaseId={currentPhaseIdForTask}
                />
            )}

            {/* Find Contractors Modal */}
            <FindContractorsModal
                isOpen={isFindContractorsModalOpen}
                onClose={() => setIsFindContractorsModalOpen(false)}
                onPostJob={handlePostJob}
                onBrowsePros={handleBrowsePros}
                taskName={activeTaskForContractors?.name}
            />

            {/* Browse Pros Modal */}
            {isBrowseProsModalOpen && activeTaskForContractors && (
                <BrowseProsModal
                    isOpen={isBrowseProsModalOpen}
                    onClose={() => setIsBrowseProsModalOpen(false)}
                    trade={activeTaskForContractors.suggestedTrade}
                    postcode="2000" // Ideally get from project/user context
                    taskId={activeTaskForContractors.id}
                    onInvite={handleInviteSent}
                />
            )}
        </DndProvider>
    );
};

export default AIProjectManager;
